/**
 * Custom Hook: useChannelPartner
 * Provides channel partner-related API calls and state management
 *
 * Usage:
 * const {
 *   pendingPayments,
 *   commissions,
 *   loading,
 *   error,
 *   submitApplication,
 *   createAgreement,
 *   getPaymentHistory
 * } = useChannelPartner();
 */

import { useState, useCallback } from 'react';
import {
  ChannelPartnerOnboardingAPI,
  ChannelPartnerAgreementAPI,
  ChannelPartnerCommissionAPI,
  ChannelPartnerPaymentAPI,
  ServicePartnerPaymentAPI
} from '../services/channelPartnerService';

export const useChannelPartner = () => {
  const [applications, setApplications] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [servicePartnerPayments, setServicePartnerPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Create a new channel partner
   */
  const createChannelPartner = useCallback(async (partnerData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ChannelPartnerOnboardingAPI.createChannelPartner(partnerData);
      // Refresh applications list
      await fetchApplications();
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to create channel partner';
      setError(errorMsg);
      console.error('useChannelPartner - createChannelPartner error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Submit channel partner onboarding application
   */
  const submitApplication = useCallback(async (applicationData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ChannelPartnerOnboardingAPI.submitApplication(applicationData);
      // Refresh applications list
      await fetchApplications();
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to submit application';
      setError(errorMsg);
      console.error('useChannelPartner - submitApplication error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch all onboarding applications
   */
  const fetchApplications = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await ChannelPartnerOnboardingAPI.getApplications(params);
      setApplications(data);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch applications';
      setError(errorMsg);
      console.error('useChannelPartner - fetchApplications error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Approve onboarding application
   */
  const approveApplication = useCallback(async (applicationId, approvalData = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ChannelPartnerOnboardingAPI.approveApplication(
        applicationId,
        approvalData
      );
      // Refresh applications list
      await fetchApplications();
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to approve application';
      setError(errorMsg);
      console.error('useChannelPartner - approveApplication error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reject onboarding application
   */
  const rejectApplication = useCallback(async (applicationId, reason = '') => {
    setLoading(true);
    setError(null);
    try {
      const response = await ChannelPartnerOnboardingAPI.rejectApplication(
        applicationId,
        reason
      );
      // Refresh applications list
      await fetchApplications();
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to reject application';
      setError(errorMsg);
      console.error('useChannelPartner - rejectApplication error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create partnership agreement
   */
  const createAgreement = useCallback(async (agreementData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ChannelPartnerAgreementAPI.createAgreement(agreementData);
      // Refresh agreements list
      await fetchAgreements();
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to create agreement';
      setError(errorMsg);
      console.error('useChannelPartner - createAgreement error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch agreements by partner ID
   */
  const fetchAgreements = useCallback(async (partnerId = '') => {
    setLoading(true);
    setError(null);
    try {
      const data = partnerId
        ? await ChannelPartnerAgreementAPI.getAgreementsByPartner(partnerId)
        : [];
      setAgreements(data);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch agreements';
      setError(errorMsg);
      console.error('useChannelPartner - fetchAgreements error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Activate partnership agreement
   */
  const activateAgreement = useCallback(async (agreementId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ChannelPartnerAgreementAPI.activateAgreement(agreementId);
      // Refresh agreements list
      await fetchAgreements();
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to activate agreement';
      setError(errorMsg);
      console.error('useChannelPartner - activateAgreement error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch channel partner commissions
   */
  const fetchCommissions = useCallback(async (partnerId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await ChannelPartnerCommissionAPI.getCommissions(partnerId);
      setCommissions(data);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch commissions';
      setError(errorMsg);
      console.error('useChannelPartner - fetchCommissions error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch pending channel partner commissions
   */
  const fetchPendingCommissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ChannelPartnerCommissionAPI.getPendingCommissions();
      setCommissions(data);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch pending commissions';
      setError(errorMsg);
      console.error('useChannelPartner - fetchPendingCommissions error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch pending channel partner payments
   */
  const fetchPendingPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ChannelPartnerPaymentAPI.getPendingPayments();
      setPendingPayments(data);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch pending payments';
      setError(errorMsg);
      console.error('useChannelPartner - fetchPendingPayments error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch payment history for a partner
   */
  const fetchPaymentHistory = useCallback(async (partnerId, params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await ChannelPartnerPaymentAPI.getPaymentHistory(partnerId, params);
      setPaymentHistory(data);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch payment history';
      setError(errorMsg);
      console.error('useChannelPartner - fetchPaymentHistory error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Approve channel partner payment
   */
  const approvePayment = useCallback(async (paymentId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ChannelPartnerPaymentAPI.approvePayment(paymentId);
      // Refresh pending payments
      await fetchPendingPayments();
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to approve payment';
      setError(errorMsg);
      console.error('useChannelPartner - approvePayment error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Process payment (bank transfer)
   */
  const processPayment = useCallback(async (paymentId, paymentDetails) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ChannelPartnerPaymentAPI.processPayment(
        paymentId,
        paymentDetails
      );
      // Refresh pending payments
      await fetchPendingPayments();
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to process payment';
      setError(errorMsg);
      console.error('useChannelPartner - processPayment error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch pending service partner payments
   */
  const fetchPendingServicePartnerPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ServicePartnerPaymentAPI.getPendingPayments();
      setServicePartnerPayments(data);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch service partner payments';
      setError(errorMsg);
      console.error('useChannelPartner - fetchPendingServicePartnerPayments error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Approve service partner payment
   */
  const approveServicePartnerPayment = useCallback(async (paymentId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ServicePartnerPaymentAPI.approvePayment(paymentId);
      // Refresh pending payments
      await fetchPendingServicePartnerPayments();
      return response;
    } catch (err) {
      const errorMsg = err.message || 'Failed to approve service partner payment';
      setError(errorMsg);
      console.error('useChannelPartner - approveServicePartnerPayment error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    applications,
    agreements,
    commissions,
    pendingPayments,
    paymentHistory,
    servicePartnerPayments,
    loading,
    error,

    // Onboarding methods
    createChannelPartner,
    submitApplication,
    fetchApplications,
    approveApplication,
    rejectApplication,

    // Agreement methods
    createAgreement,
    fetchAgreements,
    activateAgreement,

    // Commission methods
    fetchCommissions,
    fetchPendingCommissions,

    // Payment methods
    fetchPendingPayments,
    fetchPaymentHistory,
    approvePayment,
    processPayment,

    // Service partner payment methods
    fetchPendingServicePartnerPayments,
    approveServicePartnerPayment,

    // Error handling
    clearError
  };
};

export default useChannelPartner;
